/**
 * Parser eksportu listy wydarzeń z Facebooka (CSV z Web Scrapera / Instant Data Scraper).
 *
 * Kolumny (klasy CSS FB — nazwy bywają różne, mapujemy po treści):
 * - data: "SAT, SEP 5 AT 3:30 PM" / "SAT, AUG 22 - AUG 23"
 * - nazwa wydarzenia
 * - czysty link: https://www.facebook.com/events/...
 * - meta: "N people interested · lokalizacja"
 */

export interface FacebookCsvEvent {
  name: string;
  date: string;
  time: string;
  address: string;
  city: string;
  link: string;
  type: string;
  organizer: string;
  source: string;
  multiDay: boolean;
  endDate: string;
  showPrice: boolean;
  description: string;
}

const MONTHS: Record<string, number> = {
  JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
  JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12,
};

const WEEKDAYS: Record<string, number> = {
  SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6,
};

function normalizeSpaces(value: string): string {
  return value.replace(/[\u00A0\u202F\u2009]/g, ' ').replace(/\s+/g, ' ').trim();
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toIsoDate(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

function parseCsvRows(content: string): string[][] {
  const lines = content.split(/\r?\n/).filter((line) => line.trim());
  return lines.map(parseCsvLine);
}

function looksLikeFacebookDate(value: string): boolean {
  return /^(TODAY|TOMORROW|THIS\s+\w+|(MON|TUE|WED|THU|FRI|SAT|SUN),)/i.test(value);
}

function looksLikeLocationField(value: string): boolean {
  if (!value || value === '·' || value === '[object Object]') return false;
  if (/^https?:\/\//i.test(value)) return false;
  if (looksLikeFacebookDate(value)) return false;
  if (/people (interested|going)/i.test(value) || /^Online event/i.test(value)) return true;
  if (/\d{2}\s*-\s*\d{3}/.test(value)) return true;
  if (/,\s*(Poland|Polska)\b/i.test(value)) return true;
  if (/^(ul\.?|ulica)\s/i.test(value)) return true;
  return false;
}

/** Wykrywa wiersz wydarzenia FB po linku + nazwie (nagłówki CSS są niestabilne). */
function mapFacebookRow(fields: string[]): {
  name: string;
  dateRaw: string;
  link: string;
  meta: string;
} | null {
  const cleaned = fields.map((f) => normalizeSpaces(f));
  const eventLinks = cleaned.filter((f) =>
    /^https:\/\/www\.facebook\.com\/events\/\d+/i.test(f)
  );
  if (eventLinks.length === 0) return null;

  // Preferuj pierwszy link wydarzenia (kolejne bywają stronami organizatora)
  const link = eventLinks[0].replace(/\?.*$/, '').replace(/\/?$/, '/');

  let dateRaw = cleaned.find((f) => looksLikeFacebookDate(f)) || '';
  let name = '';
  let meta = cleaned.find((f) => looksLikeLocationField(f)) || '';

  // Układ A (lista wyszukiwania): date, name, link2, "N people · adres"
  // Układ B (wydarzenia strony): date, name, organizer, organizerUrl, adres
  if (cleaned.length >= 4 && looksLikeFacebookDate(cleaned[2])) {
    dateRaw = cleaned[2];
    name = cleaned[3] || '';
  }

  if (!name || name.startsWith('http') || name === '[object Object]') {
    const nameCandidate = cleaned.find(
      (f) =>
        f &&
        !f.startsWith('http') &&
        f !== '[object Object]' &&
        f !== dateRaw &&
        f !== meta &&
        f !== '·' &&
        !/^(Interested|Going)$/i.test(f) &&
        !/people (interested|going)/i.test(f) &&
        !looksLikeLocationField(f) &&
        f.length > 2
    );
    if (nameCandidate) name = nameCandidate;
  }

  if (!meta) {
    const metaCandidate = cleaned.find(
      (f) => /people (interested|going)/i.test(f) || /^Online event/i.test(f)
    );
    if (metaCandidate) meta = metaCandidate;
  }

  if (!name || !dateRaw) return null;
  return { name, dateRaw, link, meta };
}

function parseAmPmTime(raw: string): string {
  const m = normalizeSpaces(raw).match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!m) return '';
  let hour = Number(m[1]);
  const minute = m[2] ? Number(m[2]) : 0;
  const ap = m[3].toUpperCase();
  if (ap === 'PM' && hour < 12) hour += 12;
  if (ap === 'AM' && hour === 12) hour = 0;
  return `${pad2(hour)}:${pad2(minute)}`;
}

function parseMonthDay(token: string): { month: number; day: number; year?: number } | null {
  const m = normalizeSpaces(token).match(/^([A-Z]{3})\s+(\d{1,2})(?:,\s*(\d{4}))?$/i);
  if (!m) return null;
  const month = MONTHS[m[1].toUpperCase()];
  if (!month) return null;
  return { month, day: Number(m[2]), year: m[3] ? Number(m[3]) : undefined };
}

function resolveRelativeDay(label: string, ref: Date): Date {
  const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const upper = label.toUpperCase();
  if (upper === 'TODAY') return d;
  if (upper === 'TOMORROW') {
    d.setDate(d.getDate() + 1);
    return d;
  }
  const thisMatch = upper.match(/^THIS\s+(\w+)$/);
  if (thisMatch) {
    const target = WEEKDAYS[thisMatch[1].slice(0, 3)];
    if (target !== undefined) {
      const current = d.getDay();
      let delta = target - current;
      if (delta <= 0) delta += 7;
      d.setDate(d.getDate() + delta);
      return d;
    }
  }
  return d;
}

export function parseFacebookDateTime(
  raw: string,
  referenceDate: Date = new Date()
): { date: string; time: string; multiDay: boolean; endDate: string } {
  const text = normalizeSpaces(raw);
  const yearFallback = referenceDate.getFullYear();

  // TODAY / TOMORROW / THIS SATURDAY AT 6 PM
  const relative = text.match(/^(TODAY|TOMORROW|THIS\s+\w+)(?:\s+AT\s+(.+))?$/i);
  if (relative) {
    const day = resolveRelativeDay(relative[1], referenceDate);
    return {
      date: toIsoDate(day.getFullYear(), day.getMonth() + 1, day.getDate()),
      time: relative[2] ? parseAmPmTime(relative[2]) : '',
      multiDay: false,
      endDate: '',
    };
  }

  // FRI, MAY 28, 2027 - JUN 2, 2027
  const rangeWithYears = text.match(
    /^(?:[A-Z]{3}),\s+([A-Z]{3}\s+\d{1,2},\s*\d{4})\s*-\s*([A-Z]{3}\s+\d{1,2},\s*\d{4})$/i
  );
  if (rangeWithYears) {
    const start = parseMonthDay(rangeWithYears[1]);
    const end = parseMonthDay(rangeWithYears[2]);
    if (start?.year && end?.year) {
      return {
        date: toIsoDate(start.year, start.month, start.day),
        time: '',
        multiDay: true,
        endDate: toIsoDate(end.year, end.month, end.day),
      };
    }
  }

  // SAT, AUG 22 - AUG 23  |  SAT, AUG 29 - SEP 5
  const rangeNoYear = text.match(
    /^(?:[A-Z]{3}),\s+([A-Z]{3})\s+(\d{1,2})\s*-\s*([A-Z]{3})\s+(\d{1,2})$/i
  );
  if (rangeNoYear) {
    const sm = MONTHS[rangeNoYear[1].toUpperCase()];
    const em = MONTHS[rangeNoYear[3].toUpperCase()];
    const sd = Number(rangeNoYear[2]);
    const ed = Number(rangeNoYear[4]);
    if (sm && em) {
      let year = yearFallback;
      // jeśli zakres jest „wcześniej” niż dziś (np. eksport w kolejnym roku) — zostaw bieżący rok z tytułów
      return {
        date: toIsoDate(year, sm, sd),
        time: '',
        multiDay: true,
        endDate: toIsoDate(em < sm ? year + 1 : year, em, ed),
      };
    }
  }

  // SAT, SEP 5 AT 3:30 PM
  const withTime = text.match(/^(?:[A-Z]{3}),\s+([A-Z]{3}\s+\d{1,2}(?:,\s*\d{4})?)\s+AT\s+(.+)$/i);
  if (withTime) {
    const start = parseMonthDay(withTime[1]);
    if (start) {
      const year = start.year ?? yearFallback;
      return {
        date: toIsoDate(year, start.month, start.day),
        time: parseAmPmTime(withTime[2]),
        multiDay: false,
        endDate: '',
      };
    }
  }

  // WED, AUG 5  |  WED, AUG 5, 2026
  const dateOnly = text.match(/^(?:[A-Z]{3}),\s+([A-Z]{3}\s+\d{1,2}(?:,\s*\d{4})?)$/i);
  if (dateOnly) {
    const start = parseMonthDay(dateOnly[1]);
    if (start) {
      const year = start.year ?? yearFallback;
      return {
        date: toIsoDate(year, start.month, start.day),
        time: '',
        multiDay: false,
        endDate: '',
      };
    }
  }

  return { date: '', time: '', multiDay: false, endDate: '' };
}

function stripCountry(value: string): string {
  return value.replace(/,\s*(Poland|Polska)\s*$/i, '').trim();
}

function stripGminaSuffix(value: string): string {
  return value
    .replace(/\s*\(Gmina\)\s*$/i, '')
    .replace(/\s+gmina\s*$/i, '')
    .replace(/^Gmina\s+/i, '')
    .trim();
}

/** "KobierzyceKobierzyce" / "Jaworzyna ŚląskaJaworzyna Śląska" → jedna nazwa */
function dedupeRepeatedText(value: string): string {
  const s = value.trim();
  for (let i = Math.floor(s.length / 2); i >= 2; i -= 1) {
    if (s.slice(0, i) === s.slice(i)) return s.slice(0, i).trim();
  }
  return s;
}

/** Eksport FB czasem skleja adres z miastem: "…PolskaJelenia Góra", "…MiliczMilicz" */
function unmashLocation(raw: string): string {
  let text = normalizeSpaces(raw);
  if (!text) return '';

  text = text.replace(/(Poland|Polska)([A-ZĄĆĘŁŃÓŚŹŻ])/i, '$1, $2');

  const postal = text.match(/^(.*?\d{2}\s*-\s*\d{3}\s+)(.+)$/);
  if (postal) {
    let rest = postal[2];
    rest = rest.replace(/,\s*(Poland|Polska)\s*,?\s*/i, ', ');
    const city = dedupeRepeatedText(stripCountry(rest).replace(/^,\s*/, ''));
    return normalizeSpaces(`${postal[1]}${city}`);
  }

  // "…WielkopolskichMilicz" — miasto doklejone na końcu bez separatora
  const glued = text.match(/^(.+?)([A-ZĄĆĘŁŃÓŚŹŻ][a-ząćęłńóśźż]+)$/);
  if (glued && glued[1].length > 10 && !/\s$/.test(glued[1])) {
    return normalizeSpaces(`${glued[1]}, ${glued[2]}`);
  }

  return dedupeRepeatedText(text);
}

/** Lokalizacja: "N people · adres" albo surowy adres z eksportu strony FB */
export function extractLocationFromMeta(meta: string): string {
  const text = normalizeSpaces(meta);
  if (!text || text === '·') return '';

  if (/^Online event$/i.test(text) || /^Online event\s*·/i.test(text)) {
    const afterOnline = text.replace(/^Online event\s*·\s*/i, '');
    if (/people (interested|going)/i.test(afterOnline)) return '';
    return afterOnline && !/people (interested|going)/i.test(afterOnline)
      ? unmashLocation(afterOnline)
      : '';
  }

  const parts = text.split(/\s*·\s*/);
  if (parts.length >= 2) {
    const last = parts[parts.length - 1].trim();
    if (/people (interested|going)/i.test(last)) return '';
    if (/^https?:\/\//i.test(last)) return '';
    return unmashLocation(last);
  }

  if (looksLikeLocationField(text) && !/people (interested|going)/i.test(text)) {
    return unmashLocation(text);
  }

  return '';
}

const CITY_FROM_NAME_HINTS: Array<[RegExp, string]> = [
  [/Kamiennej Góry/i, 'Kamienna Góra'],
  [/Jaworzyny Śląskiej/i, 'Jaworzyna Śląska'],
  [/Jeleniej Górze/i, 'Jelenia Góra'],
  [/Jeleniogórski/i, 'Jelenia Góra'],
  [/Bolesławieckie/i, 'Bolesławiec'],
  [/Milickiego|\bw Miliczu\b/i, 'Milicz'],
  [/Złotoryi/i, 'Złotoryja'],
  [/Głogowie/i, 'Głogów'],
  [/Krzelkowie/i, 'Krzelków'],
  [/Cieplicach/i, 'Cieplice'],
  [/Kobierzyce/i, 'Kobierzyce'],
];

export function extractCityFromLocation(location: string, eventName = ''): string {
  const loc = normalizeSpaces(location);
  if (!loc) {
    return extractCityFromName(eventName);
  }

  const candidates: string[] = [];

  // "in Warsaw, Poland" / "in Gierzwałd"
  const inMatch = loc.match(/\bin\s+([^,]+?)(?:,\s*(?:Poland|Polska))?$/i);
  if (inMatch) candidates.push(stripGminaSuffix(inMatch[1]));

  // kod pocztowy + miasto
  const postal = loc.match(/\d{2}\s*-\s*\d{3}\s+([^,]+)/);
  if (postal) candidates.push(stripGminaSuffix(dedupeRepeatedText(postal[1])));

  const withoutCountry = stripCountry(loc);
  const segments = withoutCountry
    .split(',')
    .map((s) => stripGminaSuffix(dedupeRepeatedText(s.trim())))
    .filter(Boolean);

  if (segments.length) {
    candidates.push(...[...segments].reverse());
  }

  candidates.push(stripGminaSuffix(withoutCountry));

  for (const c of candidates) {
    const cleaned = c
      .replace(/\s+Voivodeship$/i, '')
      .replace(/^ul\.?\s+/i, '')
      .replace(/^ulica\s+/i, '')
      .trim();
    if (!cleaned) continue;
    if (/^\d/.test(cleaned) && !/[a-ząćęłńóśźż]/i.test(cleaned)) continue;
    if (/^(ul\.?|ulica)\b/i.test(cleaned)) continue;
    if (cleaned.length < 2) continue;
    if (cleaned.split(/\s+/).length <= 4) return cleaned;
  }

  return extractCityFromName(eventName) || (segments[0] || loc);
}

function extractCityFromName(name: string): string {
  const text = normalizeSpaces(name);
  if (!text) return '';

  for (const [re, city] of CITY_FROM_NAME_HINTS) {
    if (re.test(text)) return city;
  }

  // "Święto Chleba || Jawor 2026"
  const pipe = text.match(/\|\|\s*(?:Dni\s+)?(.+?)\s+\d{4}\s*$/i);
  if (pipe) {
    const chunk = pipe[1].replace(/^['"]|['"]$/g, '').trim();
    if (chunk && !/fest/i.test(chunk)) return chunk;
  }

  // "Dni Złotoryi 2026" / "Dni Jaworzyny Śląskiej 2026"
  const dni = text.match(/\bDni\s+(.+?)\s+\d{4}\s*$/i);
  if (dni) return dni[1].trim();

  // "Święto Patrona Miasta Strzelin 2026"
  const miasta = text.match(/\bMiasta\s+([A-ZĄĆĘŁŃÓŚŹŻ][\wąćęłńóśźż-]*)/i);
  if (miasta) return miasta[1].trim();

  // "DOŻYKI - Miłosław" / "Dożynki Gminne - Olszowa 2026"
  const dash = text.match(
    /\s+[–—-]\s+([A-ZĄĆĘŁŃÓŚŹŻ][\wąćęłńóśźż-]*)(?:\s+\d{4})?$/i
  );
  if (dash) return dash[1].trim();

  const patterns = [
    /\bGminy\s+([A-ZĄĆĘŁŃÓŚŹŻ][\wąćęłńóśźż-]*(?:\s+[A-ZĄĆĘŁŃÓŚŹŻ][\wąćęłńóśźż-]*)?)/i,
    /\bGminie\s+([A-ZĄĆĘŁŃÓŚŹŻ][\wąćęłńóśźż-]*(?:\s+[A-ZĄĆĘŁŃÓŚŹŻ][\wąćęłńóśźż-]*)?)/i,
    /\bGmina\s+([A-ZĄĆĘŁŃÓŚŹŻ][\wąćęłńóśźż-]*(?:\s+[A-ZĄĆĘŁŃÓŚŹŻ][\wąćęłńóśźż-]*)?)/i,
    // "w Jeleniej Górze" / "w Cieplicach" — do 3 słów
    /\bw\s+([A-ZĄĆĘŁŃÓŚŹŻ][\wąćęłńóśźż-]*(?:\s+[A-ZĄĆĘŁŃÓŚŹŻ][\wąćęłńóśźż-]*){0,2})/i,
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (m) return m[1].trim();
  }

  return '';
}

export function parseFacebookEventsCsv(
  content: string,
  options: { referenceDate?: Date; type?: string } = {}
): { events: FacebookCsvEvent[]; skipped: number } {
  const rows = parseCsvRows(content);
  if (rows.length < 2) return { events: [], skipped: 0 };

  const referenceDate = options.referenceDate ?? new Date();
  const type = options.type ?? 'Festiwal';
  const events: FacebookCsvEvent[] = [];
  let skipped = 0;
  const seenLinks = new Set<string>();

  // pomiń nagłówek
  for (let i = 1; i < rows.length; i += 1) {
    const mapped = mapFacebookRow(rows[i]);
    if (!mapped) {
      skipped += 1;
      continue;
    }

    const { date, time, multiDay, endDate } = parseFacebookDateTime(mapped.dateRaw, referenceDate);
    const address = extractLocationFromMeta(mapped.meta);
    const city = extractCityFromLocation(address, mapped.name);

    if (!mapped.name || !date || !city) {
      skipped += 1;
      continue;
    }

    if (seenLinks.has(mapped.link)) {
      skipped += 1;
      continue;
    }
    seenLinks.add(mapped.link);

    events.push({
      name: mapped.name,
      date,
      time,
      address,
      city,
      link: mapped.link,
      type,
      organizer: 'Facebook',
      source: 'facebook',
      multiDay,
      endDate,
      showPrice: false,
      description: '',
    });
  }

  return { events, skipped };
}

export function isFacebookEventsCsv(content: string): boolean {
  const head = content.slice(0, 2000);
  return (
    /facebook\.com\/events\//i.test(head) ||
    (/x1i10hfl/i.test(head) && /x1lliihq/i.test(head))
  );
}
